import * as React from 'react';
import { clsx } from 'clsx';

export type NumericInputState = 'default' | 'error' | 'success';

export interface NumericInputProps {
  /**
   * The label for the numeric input
   */
  label?: string;
  /**
   * The current value
   */
  value?: number;
  /**
   * Callback when value changes
   */
  onChange?: (value: number | undefined) => void;
  /**
   * Minimum allowed value
   */
  min?: number;
  /**
   * Maximum allowed value
   */
  max?: number;
  /**
   * Step increment/decrement value
   * @default 1
   */
  step?: number;
  /**
   * The validation state
   * @default 'default'
   */
  state?: NumericInputState;
  /**
   * Optional error message
   */
  error?: string;
  /**
   * Optional helper text
   */
  helperText?: string;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const labelStyles: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#2f2f2f',
  marginBottom: '4px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const helperTextStyles: React.CSSProperties = {
  fontSize: '13px',
  margin: '0',
  marginTop: '2px',
  color: '#595959',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lineHeight: '1.4',
};

const errorTextStyles: React.CSSProperties = {
  ...helperTextStyles,
  color: '#a62323', // destructive-600
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const containerStyles = {
  base: {
    display: 'flex',
    alignItems: 'center',
    height: '46px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    overflow: 'hidden',
  },
  states: {
    default: {
      border: '1px solid #d1d1d1', // grey-300
    },
    defaultFocus: {
      borderColor: '#3cad51', // brand-500
      outline: '3px solid rgba(60, 173, 81, 0.2)',
    },
    error: {
      border: '1px solid #c93232', // destructive-500
    },
    errorFocus: {
      borderColor: '#c93232',
      outline: '3px solid rgba(201, 50, 50, 0.2)',
    },
    success: {
      border: '1px solid #16a33d', // success-500
    },
    successFocus: {
      borderColor: '#16a33d',
      outline: '3px solid rgba(22, 163, 61, 0.2)',
    },
    disabled: {
      backgroundColor: '#f8f8f8', // grey-050
      borderColor: '#efefef', // grey-100
      cursor: 'not-allowed',
    },
  },
};

const buttonStyles: React.CSSProperties = {
  width: '46px',
  height: '100%',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '16px',
  color: '#2f2f2f',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  userSelect: 'none' as const,
};

const inputStyles: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  textAlign: 'center',
  fontSize: '13px',
  color: '#2f2f2f',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  padding: '0 8px',
  appearance: 'textfield' as any,
};

/**
 * NumericInput component - Arbor Design System
 *
 * A number input with increment/decrement buttons.
 * Enforces numeric rules (min, max, step) and prevents non-numeric input.
 */
export const NumericInput = React.forwardRef<HTMLDivElement, NumericInputProps>(
  (
    {
      label,
      value,
      onChange,
      min,
      max,
      step = 1,
      state = 'default',
      error,
      helperText,
      disabled = false,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const inputId = React.useId();
    const helperTextId = React.useId();
    const errorId = React.useId();

    const clampValue = (val: number): number => {
      let clamped = val;
      if (min !== undefined && clamped < min) clamped = min;
      if (max !== undefined && clamped > max) clamped = max;
      return clamped;
    };

    const handleIncrement = () => {
      if (disabled) return;
      const currentValue = value ?? 0;
      const newValue = currentValue + step;
      const clampedValue = clampValue(newValue);
      if (clampedValue !== currentValue) {
        onChange?.(clampedValue);
      }
    };

    const handleDecrement = () => {
      if (disabled) return;
      const currentValue = value ?? 0;
      const newValue = currentValue - step;
      const clampedValue = clampValue(newValue);
      if (clampedValue !== currentValue) {
        onChange?.(clampedValue);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow empty input
      if (inputValue === '' || inputValue === '-') {
        onChange?.(undefined);
        return;
      }

      // Parse as number
      const numValue = parseFloat(inputValue);

      // Only update if it's a valid number
      if (!isNaN(numValue)) {
        const clampedValue = clampValue(numValue);
        onChange?.(clampedValue);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, minus sign, decimal point
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '-', '.'];

      if (allowedKeys.includes(e.key)) {
        // Handle arrow keys for increment/decrement
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          handleIncrement();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          handleDecrement();
        }
        return;
      }

      // Only allow numbers
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    };

    const currentState = error ? 'error' : state;
    const stateStyles = containerStyles.states[currentState];
    const focusStyles =
      currentState === 'error'
        ? containerStyles.states.errorFocus
        : currentState === 'success'
        ? containerStyles.states.successFocus
        : containerStyles.states.defaultFocus;

    const containerStyle: React.CSSProperties = {
      ...containerStyles.base,
      ...stateStyles,
      ...(isFocused && !disabled && focusStyles),
      ...(disabled && containerStyles.states.disabled),
    };

    const isDecrementDisabled = disabled || (min !== undefined && value !== undefined && value <= min);
    const isIncrementDisabled = disabled || (max !== undefined && value !== undefined && value >= max);

    // Button styles for success state (green outlined circles)
    const getButtonStyle = (isDisabled: boolean) => {
      const baseStyle: React.CSSProperties = {
        ...buttonStyles,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        color: isDisabled ? '#7e7e7e' : '#2f2f2f',
      };

      // Add green circle border for success state
      if (currentState === 'success' && !isDisabled) {
        return {
          ...baseStyle,
          color: '#16a33d', // success-500
          border: '2px solid #16a33d',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
        };
      }

      return baseStyle;
    };

    return (
      <div className={clsx('arbor-numeric-input-wrapper', className)} style={style} ref={ref} data-testid={dataTestId}>
        {label && (
          <label htmlFor={inputId} style={labelStyles}>
            {label}
          </label>
        )}

        <div style={containerStyle}>
          <button
            type="button"
            onClick={handleDecrement}
            disabled={isDecrementDisabled}
            style={getButtonStyle(isDecrementDisabled)}
            onMouseEnter={(e) => {
              if (!isDecrementDisabled) {
                e.currentTarget.style.backgroundColor = '#f8f8f8';
                e.currentTarget.style.borderRadius = '99px';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderRadius = currentState === 'success' ? '50%' : '0';
            }}
            aria-label="Decrement"
          >
            −
          </button>

          <input
            ref={inputRef}
            id={inputId}
            type="text"
            inputMode="numeric"
            value={value !== undefined ? value : ''}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : helperText ? helperTextId : undefined}
            style={{
              ...inputStyles,
              color: disabled ? '#7e7e7e' : '#2f2f2f',
              cursor: disabled ? 'not-allowed' : 'text',
            }}
          />

          <button
            type="button"
            onClick={handleIncrement}
            disabled={isIncrementDisabled}
            style={getButtonStyle(isIncrementDisabled)}
            onMouseEnter={(e) => {
              if (!isIncrementDisabled) {
                e.currentTarget.style.backgroundColor = '#f8f8f8';
                e.currentTarget.style.borderRadius = '99px';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderRadius = currentState === 'success' ? '50%' : '0';
            }}
            aria-label="Increment"
          >
            +
          </button>
        </div>

        {error && (
          <p id={errorId} style={errorTextStyles}>
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperTextId} style={helperTextStyles}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

NumericInput.displayName = 'NumericInput';
